import proj4 from 'proj4';
import { get as getProjection, addEquivalentTransforms } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import { PROJECTIONS as EPSG4326_PROJECTIONS } from 'ol/proj/epsg4326'; // @ts-ignore

import phi2z from 'proj4/lib/common/phi2z'; // @ts-ignore

import tsfnz from 'proj4/lib/common/tsfnz'; // @ts-ignore

import adjustLon from 'proj4/lib/common/adjust_lon';
import { FORTPI, R2D, EPSLN, HALF_PI // @ts-ignore
} from 'proj4/lib/constants/values';
const semiMajorAxis = 6378137;
/**
 * @param {number} latitude
 */

const projectLat = latitude => {
  const flattening = 1 / 298.257223563;
  const semiMinorAxis = semiMajorAxis * (1 - flattening);
  const latRad = latitude * Math.PI / 180;
  const axisRatio = semiMinorAxis / semiMajorAxis;
  const eccentricity = Math.sqrt(1 - axisRatio * axisRatio);
  let con = eccentricity * Math.sin(latRad);
  con = Math.pow((1 - con) / (1 + con), eccentricity * 0.5);
  return -semiMinorAxis * Math.log(Math.tan(0.5 * (HALF_PI - latRad)) / con);
};

const boundLon = Math.PI * semiMajorAxis;
export const WORLD_EXTENT = [-180, -80, 180, 84];
export const EXTENT = [-boundLon, projectLat(-80), boundLon, projectLat(84)];
export const INFINITE_EXTENT = [-Infinity, projectLat(-80), Infinity, projectLat(84)]; // TODO: Use this for MPM-1087
// export const EXTENT = [
//   -HALF_SIZE, -HALF_SIZE,
//   HALF_SIZE, HALF_SIZE,
// ];

const proj4Defs = '+proj=merc +over +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs';
proj4.defs('EPSG:3395', proj4Defs);
register(proj4);
const proj3395 = proj4('EPSG:3395');
getProjection('EPSG:3395').setExtent(EXTENT);
getProjection('EPSG:3395').setGlobal(true);
/**
 * @ignore
 * TODO: HOTFIX for Longitude Wrapping
 * Proj4 does not support `+lon_wrap` and `+over` parameters:
 * https://proj.org/usage/projections.html?highlight=definition#cartographic-projection
 * As a hot fix, we override transformation function from `proj4`
 *
 * Issues:
 * Transform Lat/Lon coordinates for projection 3395 when world wrap horizontally: https://jiraeu.epam.com/browse/MPM-1207
 * Add +lon_wrap support: https://github.com/proj4js/proj4js/issues/180
 * Add +over support: https://github.com/proj4js/proj4js/issues/324
 *
 * Instead of register `proj4` to `OpenLayers,
 * we created custom projection with transformation functions(see below)
 * register(proj4);
 * const projection = getProjection('EPSG:3395');
 * projection.setExtent(EXTENT);
 * projection.setGlobal(true);
 */
// @ts-ignore

if (proj3395 && proj3395.oProj) {
  // @ts-ignore
  proj3395.oProj.forward = function forward(p) {
    const lon = p.x;
    const lat = p.y; // convert to radians

    if (lat * R2D > 90 && lat * R2D < -90 && lon * R2D > 180 && lon * R2D < -180) {
      return null;
    }

    let x;
    let y;

    if (Math.abs(Math.abs(lat) - HALF_PI) <= EPSLN) {
      return null;
    }

    const lonDiff = this.over ? lon - this.long0 : adjustLon(lon - this.long0);

    if (this.sphere) {
      x = this.x0 + this.a * this.k0 * lonDiff;
      y = this.y0 + this.a * this.k0 * Math.log(Math.tan(FORTPI + 0.5 * lat));
    } else {
      const sinphi = Math.sin(lat);
      const ts = tsfnz(this.e, lat, sinphi);
      x = this.x0 + this.a * this.k0 * lonDiff;
      y = this.y0 - this.a * this.k0 * Math.log(ts);
    }

    return {
      x,
      y
    };
  }; // @ts-ignore


  proj3395.oProj.inverse = function inverse(p) {
    const x = p.x - this.x0;
    const y = p.y - this.y0;
    let lat;
    let lon;

    if (this.sphere) {
      lat = HALF_PI - 2 * Math.atan(Math.exp(-y / (this.a * this.k0)));
    } else {
      const ts = Math.exp(-y / (this.a * this.k0));
      lat = phi2z(this.e, ts);

      if (lat === -9999) {
        return null;
      }
    }

    lon = this.long0 + x / (this.a * this.k0);

    if (!this.over) {
      lon = adjustLon(this.long0 + x / (this.a * this.k0));
    }

    return {
      x: lon,
      y: lat
    };
  };
}

export function fromEPSG4326(input, optOutput, optDimension) {
  const dimension = optDimension > 1 ? optDimension : 2;
  let output = optOutput; // @ts-ignore

  if (output === undefined) {
    if (dimension > 2) {
      // preserve values beyond second dimension
      output = input.slice();
    } else {
      output = new Array(input.length);
    }
  }

  for (let i = 0; i < input.length; i += dimension) {
    // @ts-ignore
    const [x, y] = proj4('EPSG:4326', proj3395).forward([input[i], input[i + 1]]);
    output[i] = x;
    output[i + 1] = y;
  }

  return output;
}
export function toEPSG4326(input, optOutput, optDimension) {
  const dimension = optDimension > 1 ? optDimension : 2;
  let output = optOutput;

  if (output === undefined) {
    if (dimension > 2) {
      // preserve values beyond second dimension
      output = input.slice();
    } else {
      output = new Array(input.length);
    }
  }

  for (let i = 0; i < input.length; i += dimension) {
    // @ts-ignore
    const [x, y] = proj4('EPSG:4326', proj3395).inverse([input[i], input[i + 1]]);
    output[i] = x;
    output[i + 1] = y;
  }

  return output;
}
addEquivalentTransforms(EPSG4326_PROJECTIONS, [getProjection('EPSG:3395')], fromEPSG4326, toEPSG4326);